import { captureException } from '@sentry/react';
import { MutationCache, QueryCache } from '@tanstack/react-query';
import { extractErrorMessage } from './extractErrorMessage';

type PushErrorRef = { current: (message: string) => void };

type ReportContext = {
  mechanism: 'tanstack-mutation' | 'tanstack-query';
  code?: string;
  key?: string;
  skip?: boolean;
};

// TanStack Query catches mutation/query rejections internally, so they never
// become unhandled rejections and Sentry's browser SDK never auto-captures
// them. Report them here so handled failures aren't a monitoring blind spot.
function reportErrorToSentry(error: unknown, context: ReportContext) {
  if (context.skip) return;
  // A record the caller asked for is not there. That is the server answering
  // the question, not a fault, whatever the call site — a client can always
  // hold an ID for something another user has since deleted.
  if (context.code === 'NOT_FOUND') return;

  captureException(error, {
    tags: {
      mechanism: context.mechanism,
      errorCode: context.code ?? 'unknown',
    },
    extra: { key: context.key },
  });
}

export function createQueryClientCaches(pushErrorRef: PushErrorRef) {
  return {
    queryCache: new QueryCache({
      onError: (error, query) => {
        const { message, code } = extractErrorMessage(error);
        if (query.meta?.errorMessage) {
          const customMessage =
            typeof query.meta.errorMessage === 'string'
              ? query.meta.errorMessage
              : undefined;
          pushErrorRef.current(customMessage ?? message);
        }
        reportErrorToSentry(error, {
          mechanism: 'tanstack-query',
          code,
          key: query.queryHash,
          skip: query.meta?.skipSentryReport,
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const { message, code } = extractErrorMessage(error);
        if (!mutation.meta?.skipGlobalErrorToast) {
          const customMessage =
            typeof mutation.meta?.errorMessage === 'string'
              ? mutation.meta.errorMessage
              : undefined;
          pushErrorRef.current(customMessage ?? message);
        }
        reportErrorToSentry(error, {
          mechanism: 'tanstack-mutation',
          code,
          key: mutation.options.mutationKey?.join('.'),
          skip: mutation.meta?.skipSentryReport,
        });
      },
    }),
  };
}
